import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Global auth flows as bottom sheets (login, register, OTP, reset password).
 * Provider wraps the app; AuthSheetHost renders the auth overlay. Screens call useAuthSheets() to open/close.
 *
 * `presentation` on login/register selects overlay motion in `AuthSheetHost` (`authOverlayPresentation.ts`).
 * Omitted → `default` (bottom sheet). Main onboarding uses inline auth on `MainScreen` (no context open).
 */

export type AuthSheetPresentation = 'default' | 'mainScreen';

export type AuthSheetState =
  | null
  | { kind: 'login'; presentation?: AuthSheetPresentation }
  | { kind: 'register'; subData?: unknown; presentation?: AuthSheetPresentation }
  | { kind: 'otp'; params: Record<string, unknown> }
  | { kind: 'reset'; params: Record<string, unknown> };

export type AuthSheetApi = {
  openLogin: (opts?: { presentation?: AuthSheetPresentation }) => void;
  openRegister: (opts?: { subData?: unknown; presentation?: AuthSheetPresentation }) => void;
  openOtp: (params: Record<string, unknown>) => void;
  openReset: (params: Record<string, unknown>) => void;
  dismiss: () => void;
  /** Replace current sheet without closing modal (e.g. login → register). */
  state: AuthSheetState;
};

const AuthSheetContext = createContext<AuthSheetApi | null>(null);

function resolveRegisterPresentation(
  prev: AuthSheetState | null,
  opts?: { subData?: unknown; presentation?: AuthSheetPresentation },
): AuthSheetPresentation | undefined {
  if (opts?.presentation != null) {
    return opts.presentation;
  }
  // Do not inherit `mainScreen` into classic center cards when caller omits presentation.
  if (prev?.kind === 'login' || prev?.kind === 'register') {
    return prev.presentation === 'mainScreen' ? 'mainScreen' : 'default';
  }
  return 'default';
}

function resolveLoginPresentation(
  prev: AuthSheetState | null,
  opts?: { presentation?: AuthSheetPresentation },
): AuthSheetPresentation | undefined {
  if (opts?.presentation != null) {
    return opts.presentation;
  }
  if (prev?.kind === 'login' || prev?.kind === 'register') {
    return prev.presentation === 'mainScreen' ? 'mainScreen' : 'default';
  }
  return 'default';
}

export function AuthSheetProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthSheetState>(null);

  const openLogin = useCallback((opts?: { presentation?: AuthSheetPresentation }) => {
    setState(prev => {
      const presentation = resolveLoginPresentation(prev, opts);
      return {
        kind: 'login',
        ...(presentation != null ? { presentation } : {}),
      };
    });
  }, []);

  const openRegister = useCallback((opts?: { subData?: unknown; presentation?: AuthSheetPresentation }) => {
    setState(prev => {
      const presentation = resolveRegisterPresentation(prev, opts);
      return {
        kind: 'register',
        ...(opts?.subData !== undefined ? { subData: opts.subData } : {}),
        ...(presentation != null ? { presentation } : {}),
      };
    });
  }, []);

  const openOtp = useCallback((params: Record<string, unknown>) => {
    setState({ kind: 'otp', params });
  }, []);

  const openReset = useCallback((params: Record<string, unknown>) => {
    setState({ kind: 'reset', params });
  }, []);

  const dismiss = useCallback(() => {
    setState(null);
  }, []);

  const value = useMemo(
    () => ({
      openLogin,
      openRegister,
      openOtp,
      openReset,
      dismiss,
      state,
    }),
    [state, openLogin, openRegister, openOtp, openReset, dismiss],
  );

  return <AuthSheetContext.Provider value={value}>{children}</AuthSheetContext.Provider>;
}

export function useAuthSheets(): AuthSheetApi {
  const ctx = useContext(AuthSheetContext);
  if (!ctx) {
    throw new Error('useAuthSheets must be used within AuthSheetProvider');
  }
  return ctx;
}
